import React from 'react';
import { Target, Award, Users, TrendingUp, Zap, Shield } from 'lucide-react';
import CTA from '../components/CTA';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description: 'To empower ecommerce businesses with world-class fulfillment solutions that drive growth and customer satisfaction.'
    },
    {
      icon: Award,
      title: 'Quality First',
      description: 'Every product goes through rigorous quality checks. We stand behind every package we ship with our quality guarantee.'
    },
    {
      icon: Users,
      title: 'Customer Focus',
      description: 'Your success is our success. We provide dedicated support and work as an extension of your team.'
    },
    {
      icon: TrendingUp,
      title: 'Innovation',
      description: 'We continuously invest in technology and processes to provide the most efficient fulfillment solutions.'
    }
  ];

  return (
    <div>
      {/* Creative Hero Section */}
      <div className="relative bg-gradient-to-br from-purple-600 via-purple-700 to-pink-700 py-40 mt-20 overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 30% 50%, white 2px, transparent 2px), radial-gradient(circle at 70% 80%, white 2px, transparent 2px)',
          backgroundSize: '80px 80px'
        }}></div>
        <div className="absolute top-20 left-10 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-pink-400/20 rounded-full blur-3xl animate-float" style={{animationDelay: '1s'}}></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 mb-8">
              <Zap size={20} className="text-yellow-300" />
              <span className="text-white font-bold text-sm">About Our Company</span>
            </div>
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-black text-white mb-6 leading-none">
              <span className="block">Building</span>
              <span className="block text-purple-200">The Future of</span>
              <span className="block">Fulfillment</span>
            </h1>
            <p className="text-2xl text-purple-100 max-w-3xl mx-auto leading-relaxed">
              Your trusted partner in Print on Demand and fulfillment excellence
            </p>
          </div>
        </div>
      </div>

      {/* Story Section - Creative Asymmetric Layout */}
      <section className="py-32 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-100 rounded-full opacity-30 blur-3xl"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <div>
              <div className="inline-flex items-center space-x-2 bg-purple-50 rounded-full px-5 py-2 mb-6">
                <div className="w-2 h-2 bg-purple-600 rounded-full animate-pulse"></div>
                <span className="text-purple-600 font-bold text-sm uppercase tracking-wide">Our Story</span>
              </div>
              
              <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-8">
                <span className="block">Delivering</span>
                <span className="gradient-text bg-gradient-to-r from-purple-600 to-pink-600" style={{
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>Excellence</span>
                <span className="block">Since Day One</span>
              </h2>
              
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p className="border-l-4 border-purple-600 pl-6">
                  BlueLanternOutFitters LLC was founded with a simple mission: to make world-class 
                  fulfillment accessible to businesses of all sizes. What started as a small operation 
                  has grown into a global fulfillment partner.
                </p>
                <p>
                  We've built a reputation for reliability, quality, and exceptional customer service. 
                  Our team combines industry expertise with cutting-edge technology to deliver solutions 
                  that truly make a difference.
                </p>
                <p className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-2xl border-l-4 border-purple-600">
                  We're more than just a fulfillment company — we're your partners in growth. From 
                  product sourcing to global delivery, we handle the complex logistics so you can 
                  focus on what matters most: building your brand and delighting your customers.
                </p>
              </div>
            </div>

            {/* Stats Grid - Creative Design */}
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-3xl p-8 text-white shadow-2xl transform hover:scale-105 transition-all duration-300">
                <div className="text-6xl font-black mb-2">15K+</div>
                <div className="text-purple-200 text-lg font-bold">Trusted Sellers</div>
              </div>
              <div className="bg-gradient-to-br from-pink-600 to-pink-700 rounded-3xl p-8 text-white shadow-2xl transform hover:scale-105 transition-all duration-300 mt-12">
                <div className="text-6xl font-black mb-2">99.2%</div>
                <div className="text-pink-200 text-lg font-bold">On-Time Rate</div>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-3xl p-8 text-white shadow-2xl transform hover:scale-105 transition-all duration-300 -mt-12">
                <div className="text-6xl font-black mb-2">2M+</div>
                <div className="text-purple-100 text-lg font-bold">Orders Fulfilled</div>
              </div>
              <div className="bg-gradient-to-br from-purple-700 to-pink-700 rounded-3xl p-8 text-white shadow-2xl transform hover:scale-105 transition-all duration-300">
                <div className="text-6xl font-black mb-2">24/7</div>
                <div className="text-purple-200 text-lg font-bold">Support Available</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section - Creative Cards */}
      <section className="py-32 bg-gradient-to-b from-purple-50 via-white to-purple-50 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-pink-100 rounded-full opacity-30 blur-3xl"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full px-6 py-3 mb-6">
              <Shield size={20} className="text-purple-600" />
              <span className="text-purple-600 font-bold text-sm uppercase tracking-wide">Our Core Values</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">
              <span className="block">The Principles</span>
              <span className="gradient-text bg-gradient-to-r from-purple-600 to-pink-600" style={{
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>That Guide Us</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The foundation of everything we do
            </p>
          </div>

          {/* Creative Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              const gradients = [
                'from-purple-500 to-purple-600',
                'from-pink-500 to-pink-600',
                'from-purple-600 to-pink-600',
                'from-pink-600 to-purple-600'
              ];
              
              return (
                <div
                  key={index}
                  className="group relative bg-white rounded-3xl p-10 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-purple-300 transform hover:-translate-y-2"
                >
                  {/* Decorative Corner */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-50 to-transparent rounded-tr-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  
                  {/* Icon with Creative Background */}
                  <div className="relative mb-6">
                    <div className={`absolute inset-0 bg-gradient-to-br ${gradients[index % 4]} rounded-2xl blur-xl opacity-50 group-hover:opacity-70 transition-opacity`}></div>
                    <div className={`relative w-20 h-20 bg-gradient-to-br ${gradients[index % 4]} rounded-2xl flex items-center justify-center transform group-hover:rotate-12 group-hover:scale-110 transition-all duration-300`}>
                      <IconComponent size={36} className="text-white" />
                    </div>
                  </div>

                  <h3 className="text-3xl font-black text-gray-900 mb-4 group-hover:text-purple-600 transition-colors">
                    {value.title}
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {value.description}
                  </p>

                  {/* Hover Effect */}
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-purple-500/0 to-pink-500/0 group-hover:from-purple-500/5 group-hover:to-pink-500/5 transition-all duration-300 pointer-events-none"></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <CTA />
    </div>
  );
};

export default About;